import { api } from './client'
import type { ImportRosterResultDto } from './admin'

export type TenantSummaryDto = {
  id: string
  name: string
  slug: string
  isActive: boolean
  createdAt: string
  userCount: number
  hasLinkedTrainer: boolean
}

export type TenantDetailDto = {
  id: string
  name: string
  slug: string
  isActive: boolean
  createdAt: string
  userCount: number
  displayName: string | null
  contactEmail: string | null
  website: string | null
  companyNumber: string | null
  vatNumber: string | null
  planTier: string
  subscriptionStatus: string
  isComped: boolean
  hasStripeSubscription: boolean
  cancelAtPeriodEnd: boolean
  currentPeriodEndsAt: string | null
  additionalTrainerSeats: number
  platformFeeAmountPence: number
  currency: string
  country: string
  timeZoneId: string
  hasFinancialData: boolean
  liveCapacity: number
  /** How they signed up, when through the public form (null for tenants SuperAdmin created). */
  signup: TenantSignupInfoDto | null
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  billingInterval: 'Monthly' | 'Annual'
  /** Free trainer seats granted by SuperAdmin; on top of the plan's seats and any paid add-ons. */
  complimentaryTrainerSeats: number
}

export type TenantSignupInfoDto = {
  heardAbout: string | null
  heardAboutLabel: string | null
  heardAboutOther: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  referrer: string | null
  /** Null for signups from before acceptance was recorded. */
  termsAcceptedAt: string | null
  termsVersion: number | null
  privacyPolicyVersion: number | null
  signupEmail: string
  phone: string | null
  /** e.g. "Opted in (ticked at signup)", "Opted in (existing customer default)", "Opted out", "Not asked". */
  productUpdateEmails: string
}

export type CancellationFeedbackDto = {
  id: string
  tenantId: string
  tenantName: string
  createdAt: string
  outcome: 'Cancelled' | 'TrialExtended' | 'DiscountApplied' | string
  reason: string | null
  reasonLabel: string | null
  reasonDetail: string | null
  planTier: string | null
  wasTrialing: boolean
  userEmail: string | null
}

export type PlatformLegalStatusDto = {
  key: 'terms' | 'privacy-policy'
  uploaded: boolean
  version: number | null
  recordedAt: string | null
  versionCount: number
}

export type UploadedLegalDto = { key: string; version: number; isNewVersion: boolean; recordedAt: string }

export type TenantAccountDto = {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  isActive: boolean
  createdAt: string
}

export type TenantUsageDto = {
  clientCount: number
  staffMemberCount: number
  activeStaffMemberCount: number
  courseCount: number
  bookingCount: number
  upcomingBookingCount: number
  trainingRecordCount: number
  certificateCount: number
  pendingBookingEnquiryCount: number
  lastBookingCreatedAt: string | null
  lastTrainingRecordCreatedAt: string | null
}

export type SystemTemplateDto = {
  id: string
  name: string
  description: string | null
  templateType: 'Png' | 'Svg'
  thumbnailUrl: string | null
  createdAt: string
  isPortrait: boolean
}

export type EmailTemplateSummaryDto = {
  key: string
  name: string
  description: string
  subject: string
  isCustomised: boolean
  updatedAt: string | null
}

export type EmailTemplateVariableDto = {
  name: string
  description: string
  example: string
}

export type EmailTemplateDto = {
  key: string
  name: string
  description: string
  subject: string
  htmlBody: string
  isCustomised: boolean
  updatedAt: string | null
  availableVariables: EmailTemplateVariableDto[]
}

export type AccountActionDto = { allowed: boolean; reason: string | null }

export type AccountActionsDto = {
  changeEmail: AccountActionDto
  reactivate: AccountActionDto
  inviteLink: AccountActionDto
  delete: AccountActionDto
  changeRole: AccountActionDto
  /** The roles this account can be changed to. */
  roleOptions: string[]
}

export type UserSearchResultDto = {
  id: string
  email: string
  tenantId: string
  tenantName: string | null
  role: string
  isActive: boolean
  /** Active, InviteNotAccepted (never set a password) or Deactivated. */
  status: 'Active' | 'InviteNotAccepted' | 'Deactivated' | string
  clientName: string | null
  /** The trainer or staff record the login is attached to. */
  linkedTo: string | null
  createdAt: string
  actions: AccountActionsDto
}

export type LegalDocDto = {
  key: string
  title: string
  subtitle: string | null
  body: string
  version: number
  effectiveOn: string
  updatedAt: string
  updatedBy: string | null
}

export type LegalRevisionDto = {
  version: number
  savedAt: string
  savedBy: string | null
  changeNote: string | null
  title: string
  subtitle: string | null
  body: string
  effectiveOn: string
}

export type AuditLogItemDto = {
  id: string
  tenantId: string | null
  actorEmail: string | null
  action: string
  detail: string | null
  createdAt: string
}

export type AuditLogPageDto = {
  total: number
  page: number
  pageSize: number
  items: AuditLogItemDto[]
}

export type SupportRequestDto = {
  id: string
  tenantId: string
  tenantName: string
  subject: string
  message: string
  category: string
  status: string
  submittedByEmail: string
  phone: string | null
  pageUrl: string | null
  internalNotes: string | null
  createdAt: string
  resolvedAt: string | null
}

export type BackgroundJobLogDto = {
  id: string
  jobName: string
  startedAt: string
  completedAt: string | null
  status: 'Running' | 'Succeeded' | 'Failed'
  recordsProcessed: number | null
  errorMessage: string | null
  durationMs: number | null
}

export type JobSummaryDto = {
  jobName: string
  description: string
  schedule: string
  lastRun: BackgroundJobLogDto | null
}

export type JobLogPageDto = {
  total: number
  page: number
  pageSize: number
  items: BackgroundJobLogDto[]
}

export type EmailLogDto = {
  id: string
  toEmail: string
  subject: string
  category: string
  success: boolean
  errorMessage: string | null
  sentAt: string
}

export type EmailLogPageDto = {
  total: number
  page: number
  pageSize: number
  items: EmailLogDto[]
}

export type ChangelogEntryType = 'Feature' | 'Improvement' | 'Fix'

export type ChangelogEntryDto = {
  id: string
  version: string
  date: string
  type: ChangelogEntryType
  title: string
  description: string | null
  isTested: boolean
  notes: string | null
  createdAt: string
}

// ── Public release notes ("what's new") ─────────────────────────────────────

export type ReleaseNoteStatus = 'Draft' | 'Published'
export type ReleaseItemKind = 'New' | 'Improved' | 'Fixed'

export type ReleaseNoteSummaryDto = {
  id: string
  slug: string
  title: string
  date: string
  status: ReleaseNoteStatus
  summary: string | null
  itemCount: number
  publishedAt: string | null
  updatedAt: string
  sourceVersions: string | null
}

export type ReleaseNoteItemDto = {
  id: string
  kind: ReleaseItemKind
  heading: string
  body: string | null
  imageId: string | null
  imageWidth: number | null
  imageHeight: number | null
  imageAlt: string | null
}

export type ReleaseNoteDto = {
  id: string
  slug: string
  title: string
  summary: string | null
  metaDescription: string | null
  date: string
  status: ReleaseNoteStatus
  publishedAt: string | null
  updatedAt: string
  sourceVersions: string | null
  heroImageId: string | null
  heroImageWidth: number | null
  heroImageHeight: number | null
  items: ReleaseNoteItemDto[]
}

export type ReleaseNoteSaveRequest = {
  slug: string | null
  title: string
  summary: string | null
  metaDescription: string | null
  date: string
  heroImageId: string | null
  sourceVersions: string | null
  items: { id: string | null; kind: ReleaseItemKind; heading: string; body: string | null; imageId: string | null; imageAlt: string | null }[]
}

export type ReleaseNoteSettingsDto = { rebuildConfigured: boolean; publicBaseUrl: string }

/** Public, stable address of a release screenshot (the API serves it with long-lived caching). */
export function releaseImageUrl(id: string): string {
  const base = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api/v1` : '/api/v1'
  return `${base}/public/release-images/${id}`
}

export const superadminApi = {
  // ── Tenants ────────────────────────────────────────────────────────────────
  getTenants: () => api.get<TenantSummaryDto[]>('/superadmin/tenants'),

  createTenant: (data: { tenantName: string; adminEmail: string; adminPassword: string; adminFirstName: string; adminLastName: string; currency?: string; country?: string; timeZoneId?: string }) =>
    api.post<{ tenantId: string; adminUserId: string }>('/superadmin/tenants', data),

  setTenantActive: (id: string, isActive: boolean) =>
    api.patch(`/superadmin/tenants/${id}/active`, { isActive }),

  backfillAdminTrainer: (id: string) =>
    api.post(`/superadmin/tenants/${id}/backfill-admin-trainer`),

  seedDemoData: (tenantId: string, force = false) =>
    api.post<{ message: string }>(`/superadmin/tenants/${tenantId}/seed-demo`, undefined, { params: { force } }),

  impersonateTenant: (tenantId: string) =>
    api.post<{ token: string; tenantName: string }>(`/superadmin/tenants/${tenantId}/impersonate`),

  deleteTenant: (id: string, confirmName: string) =>
    api.delete<{ tenantName: string; blobsDeleted: number; blobsFailed: number }>(
      `/superadmin/tenants/${id}`, { data: { confirmName } }),

  getTenant: (id: string) =>
    api.get<TenantDetailDto>(`/superadmin/tenants/${id}`),

  getTenantUsage: (id: string) =>
    api.get<TenantUsageDto>(`/superadmin/tenants/${id}/usage`),

  updateTenant: (id: string, data: { name: string; displayName: string | null; contactEmail: string | null; website: string | null; companyNumber: string | null; vatNumber: string | null }) =>
    api.put(`/superadmin/tenants/${id}`, data),

  setTenantBilling: (id: string, data: { planTier: string; isComped: boolean }) =>
    api.post(`/superadmin/tenants/${id}/billing/plan`, data),

  setTenantPlatformFee: (id: string, data: { platformFeeAmountPence: number }) =>
    api.post(`/superadmin/tenants/${id}/billing/platform-fee`, data),

  setTenantCurrency: (id: string, data: { currency: string; country: string; timeZoneId?: string }) =>
    api.post(`/superadmin/tenants/${id}/currency`, data),

  setTenantComplimentarySeats: (id: string, seats: number) =>
    api.post(`/superadmin/tenants/${id}/complimentary-seats`, { seats }),

  setTenantLiveCapacity: (id: string, capacity: number) =>
    api.post(`/superadmin/tenants/${id}/live-capacity`, { capacity }),

  createTenantCheckoutLink: (id: string, data: { email: string; planTier: string; trialDays: number; extraTrainerSeats: number; customPriceId?: string | null; couponId?: string | null }) =>
    api.post<{ url: string }>(`/superadmin/tenants/${id}/billing/checkout-link`, data),

  getTenantAccounts: (id: string) =>
    api.get<TenantAccountDto[]>(`/superadmin/tenants/${id}/accounts`),

  addTenantAccount: (id: string, email: string, firstName: string, lastName: string) =>
    api.post(`/superadmin/tenants/${id}/accounts`, { email, firstName, lastName }),

  sendTenantWelcomeEmail: (id: string, email: string) =>
    api.post(`/superadmin/tenants/${id}/accounts/welcome-email`, { email }),

  sendTenantPasswordReset: (id: string, email: string) =>
    api.post(`/superadmin/tenants/${id}/accounts/reset-password`, { email }),

  importTenantRoster: (tenantId: string, file: File, dryRun: boolean) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<ImportRosterResultDto>(
      `/superadmin/tenants/${tenantId}/import/roster?dryRun=${dryRun}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
  },

  // ── System certificate templates ───────────────────────────────────────────
  getSystemTemplates: () =>
    api.get<SystemTemplateDto[]>('/superadmin/system-templates'),

  uploadSystemTemplate: (formData: FormData) =>
    api.post<{ id: string }>('/superadmin/system-templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateSystemTemplate: (id: string, data: { name: string; description: string | null; isPortrait: boolean }) =>
    api.put(`/superadmin/system-templates/${id}`, data),

  replaceSystemTemplateFile: (id: string, formData: FormData) =>
    api.put(`/superadmin/system-templates/${id}/file`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  deleteSystemTemplate: (id: string) =>
    api.delete(`/superadmin/system-templates/${id}`),

  // ── Legal documents ────────────────────────────────────────────────────────
  getCancellations: () => api.get<CancellationFeedbackDto[]>('/superadmin/cancellations'),

  getLegalStatus: () =>
    api.get<{ privacyPolicy: boolean; terms: boolean }>('/legal/status'),

  uploadLegalDocument: (type: 'privacy-policy' | 'terms', file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post<UploadedLegalDto>(`/superadmin/legal/${type}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  /** Which version of the Terms and Privacy Policy is live (versions are numbered automatically on upload). */
  getPlatformLegalVersions: () =>
    api.get<PlatformLegalStatusDto[]>('/superadmin/legal/versions'),

  // ── In-app legal documents (Data Processing Terms, photo guide) ─────────────
  getLegalDoc: (key: string) =>
    api.get<LegalDocDto>(`/superadmin/legal-documents/${key}`).then(r => r.data)
      .catch(err => (err?.response?.status === 404 ? null : Promise.reject(err))),
  getLegalDocRevisions: (key: string) =>
    api.get<LegalRevisionDto[]>(`/superadmin/legal-documents/${key}/revisions`).then(r => r.data),
  saveLegalDoc: (key: string, data: { title: string; subtitle: string | null; body: string; effectiveOn: string | null; requireReAcceptance: boolean; changeNote: string | null }) =>
    api.put<{ version: number }>(`/superadmin/legal-documents/${key}`, data).then(r => r.data),

  // ── Email templates ────────────────────────────────────────────────────────
  getEmailTemplates: () =>
    api.get<EmailTemplateSummaryDto[]>('/superadmin/email-templates'),

  getEmailTemplate: (key: string) =>
    api.get<EmailTemplateDto>(`/superadmin/email-templates/${key}`),

  updateEmailTemplate: (key: string, data: { subject: string; htmlBody: string }) =>
    api.put(`/superadmin/email-templates/${key}`, data),

  resetEmailTemplate: (key: string) =>
    api.post(`/superadmin/email-templates/${key}/reset`),

  // ── Support ────────────────────────────────────────────────────────────────
  searchUsers: (email: string) =>
    api.get<UserSearchResultDto[]>(`/superadmin/users/search?email=${encodeURIComponent(email)}`),

  getResetLink: (userId: string) =>
    api.post<{ link: string }>(`/superadmin/users/${userId}/reset-link`),

  getInviteLink: (userId: string) =>
    api.post<{ link: string }>(`/superadmin/users/${userId}/invite-link`),
  changeUserEmail: (userId: string, email: string) => api.put(`/superadmin/users/${userId}/email`, { email }),
  reactivateUser: (userId: string) => api.post(`/superadmin/users/${userId}/reactivate`),
  /** Soft delete: frees the email and switches the login off, keeping the history that refers to it. */
  deleteUser: (userId: string) => api.delete(`/superadmin/users/${userId}`),
  changeUserRole: (userId: string, role: string) => api.put(`/superadmin/users/${userId}/role`, { role }),

  getAuditLog: (params: { tenantId?: string; page?: number; pageSize?: number }) =>
    api.get<AuditLogPageDto>('/superadmin/audit-log', { params }),

  getSupportRequests: (status?: string) =>
    api.get<SupportRequestDto[]>('/superadmin/support-requests', { params: { status } }),

  updateSupportRequest: (id: string, data: { status: string; internalNotes: string | null }) =>
    api.put(`/superadmin/support-requests/${id}`, data),

  // ── Monitoring ─────────────────────────────────────────────────────────────
  getJobSummary: () =>
    api.get<JobSummaryDto[]>('/superadmin/jobs/summary'),

  triggerJob: (jobName: string) =>
    api.post(`/superadmin/jobs/${encodeURIComponent(jobName)}/trigger`),

  getJobLog: (params: { jobName?: string; page?: number; pageSize?: number }) =>
    api.get<JobLogPageDto>('/superadmin/jobs', { params }),

  getEmailLog: (params: { category?: string; page?: number; pageSize?: number }) =>
    api.get<EmailLogPageDto>('/superadmin/emails', { params }),

  // ── Changelog ──────────────────────────────────────────────────────────────
  getChangelog: () =>
    api.get<ChangelogEntryDto[]>('/superadmin/changelog'),

  createChangelogEntry: (data: { version: string; date: string; type: ChangelogEntryType; title: string; description: string | null; isTested: boolean; notes: string | null }) =>
    api.post<ChangelogEntryDto>('/superadmin/changelog', data),

  updateChangelogEntry: (id: string, data: { version: string; date: string; type: ChangelogEntryType; title: string; description: string | null; isTested: boolean; notes: string | null }) =>
    api.put(`/superadmin/changelog/${id}`, data),

  deleteChangelogEntry: (id: string) =>
    api.delete(`/superadmin/changelog/${id}`),

  // ── Release notes (public "what's new") ───────────────────────────────────
  getReleaseNotes: () =>
    api.get<ReleaseNoteSummaryDto[]>('/superadmin/release-notes'),

  getReleaseNote: (id: string) =>
    api.get<ReleaseNoteDto>(`/superadmin/release-notes/${id}`),

  getReleaseNoteSettings: () =>
    api.get<ReleaseNoteSettingsDto>('/superadmin/release-notes/settings'),

  createReleaseNote: (data: ReleaseNoteSaveRequest) =>
    api.post<ReleaseNoteDto>('/superadmin/release-notes', data),

  updateReleaseNote: (id: string, data: ReleaseNoteSaveRequest) =>
    api.put<ReleaseNoteDto>(`/superadmin/release-notes/${id}`, data),

  publishReleaseNote: (id: string) =>
    api.post<ReleaseNoteDto>(`/superadmin/release-notes/${id}/publish`),

  unpublishReleaseNote: (id: string) =>
    api.post<ReleaseNoteDto>(`/superadmin/release-notes/${id}/unpublish`),

  deleteReleaseNote: (id: string) =>
    api.delete(`/superadmin/release-notes/${id}`),

  createReleaseNoteFromChangelog: (versions: string[], includeFixes: boolean) =>
    api.post<ReleaseNoteDto>('/superadmin/release-notes/from-changelog', { versions, includeFixes }),

  uploadReleaseImage: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ id: string; width: number; height: number }>('/superadmin/release-notes/images', form)
  },

  rebuildMarketingSite: () =>
    api.post<{ triggered: boolean }>('/superadmin/release-notes/rebuild-site'),
}
